import { store } from '../../admin/store';
import { exitGame } from '../shiftTicTacToe/store/gameSlice';
import { clearLocalGameSnapshot } from '../game/gameLocalStorage';
import {
  getNextStartingPlayer,
  starterStorageKey,
} from '../game/alternateStartStorage';
import {
  appendScoreboardEndedMatchSig,
  isScoreboardGamePrepared,
  markScoreboardGamePrepared,
  readScoreboardData,
  readScoreboardMatchSig,
} from './scoreboardStorage';

const LOBBY_REMATCH_PENDING_KEY = 'scoreboardLobbyRematchPending';
const LOBBY_SCOREBOARD_LOCK_KEY = 'lobbyPvpScoreboardLock';
const LOCAL_RESET_PENDING_KEY = 'pendingLocalGameReset';
const LOCAL_STARTING_PLAYER_KEY = 'pendingStartingPlayer';

export function resolveScoreboardMeta(scoreboard) {
  const fromStorage = readScoreboardData() || {};
  const merged = { ...fromStorage, ...(scoreboard || {}) };
  const categoryMode =
    merged.categoryMode ||
    (merged.isLobbyPvp ? 'pvp' : merged.isPassPlay ? 'Pass & Play' : null);
  return {
    categoryMode,
    isLobbyPvp: Boolean(merged.isLobbyPvp),
    isPassPlay: Boolean(merged.isPassPlay),
    lobbyName: '',
  };
}

/**
 * Prepare a fresh game when the scoreboard is shown (alternate starter / lobby rematch).
 * Safe to call once per finished match (guarded by endedMatchSig).
 */
export async function prepareNextGameAfterScoreboard(
  scoreboard,
  { endedMatchSig, lobbyName, dispatch, resetGameAction } = {}
) {
  const meta = resolveScoreboardMeta(scoreboard);
  const matchSig =
    String(endedMatchSig ?? scoreboard?.endedMatchSig ?? readScoreboardMatchSig() ?? '').trim() ||
    '1';

  if (isScoreboardGamePrepared(matchSig)) {
    return { skipped: true, matchSig };
  }

  appendScoreboardEndedMatchSig(matchSig);

  const mode = meta.categoryMode;
  const isLobby =
    meta.isLobbyPvp || mode === 'pvp' || mode === 'online';

  clearLocalGameSnapshot(mode);

  if (isLobby) {
    markScoreboardGamePrepared(matchSig);
    return { skipped: false, matchSig, mode: 'pvp' };
  }

  const starterKey = starterStorageKey(mode, lobbyName);
  const startingPlayer = getNextStartingPlayer(starterKey);

  if (dispatch && typeof resetGameAction === 'function') {
    resetGameAction({ startingPlayer });
  } else {
    try {
      sessionStorage.setItem(LOCAL_STARTING_PLAYER_KEY, startingPlayer);
      sessionStorage.setItem(LOCAL_RESET_PENDING_KEY, '1');
    } catch {
      /* ignore */
    }
  }

  markScoreboardGamePrepared(matchSig);
  return { skipped: false, matchSig, mode, startingPlayer };
}

export function peekPendingLocalGameReset() {
  try {
    return sessionStorage.getItem(LOCAL_RESET_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

export function consumePendingLocalGameReset() {
  try {
    if (sessionStorage.getItem(LOCAL_RESET_PENDING_KEY) !== '1') return null;
    sessionStorage.removeItem(LOCAL_RESET_PENDING_KEY);
    const startingPlayer =
      sessionStorage.getItem(LOCAL_STARTING_PLAYER_KEY) || 'X';
    sessionStorage.removeItem(LOCAL_STARTING_PLAYER_KEY);
    return startingPlayer;
  } catch {
    return null;
  }
}

/**
 * Clear shiftGame Redux state and persisted snapshots so Play Again starts fresh.
 * Call from the scoreboard before navigating to /game.
 */
export function resetGameStoreForPlayAgain(categoryMode, { isLobbyPvp } = {}) {
  const mode = categoryMode || (isLobbyPvp ? 'pvp' : '');
  const isLobby = Boolean(isLobbyPvp || mode === 'pvp' || mode === 'online');

  store.dispatch(exitGame());

  clearLocalGameSnapshot(mode);
  clearLocalGameSnapshot('ai');
  clearLocalGameSnapshot('Pass & Play');

  if (isLobby) {
    clearLobbyScoreboardLock();
    armPlayAgainFromLeaderboard();
    return { isLobby: true, mode: 'pvp' };
  }

  if (!mode) {
    return { isLobby: false, mode: '' };
  }

  let startingPlayer = 'X';

  try {
    if (sessionStorage.getItem(LOCAL_RESET_PENDING_KEY) !== '1') {
      startingPlayer = getNextStartingPlayer(starterStorageKey(mode));
      sessionStorage.setItem(LOCAL_STARTING_PLAYER_KEY, startingPlayer);
      sessionStorage.setItem(LOCAL_RESET_PENDING_KEY, '1');
    } else {
      startingPlayer =
        sessionStorage.getItem(LOCAL_STARTING_PLAYER_KEY) || 'X';
    }
  } catch {
    /* ignore */
  }

  return { isLobby: false, mode, startingPlayer };
}

/** Arm rematch only when the user chooses Play Again (not while scoreboard is open). */
export function armPlayAgainFromLeaderboard() {
  try {
    sessionStorage.setItem(LOBBY_REMATCH_PENDING_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function clearPlayAgainArm() {
  try {
    sessionStorage.removeItem(LOBBY_REMATCH_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/** Lock post-match navigation so remote board resets do not pull clients back to /game. */
export function setLobbyScoreboardLock(matchSig) {
  try {
    const sig = String(matchSig ?? '').trim();
    if (sig) {
      sessionStorage.setItem(LOBBY_SCOREBOARD_LOCK_KEY, sig);
    }
  } catch {
    /* ignore */
  }
}

export function peekLobbyScoreboardLock() {
  try {
    return String(sessionStorage.getItem(LOBBY_SCOREBOARD_LOCK_KEY) ?? '').trim();
  } catch {
    return '';
  }
}

export function clearLobbyScoreboardLock() {
  try {
    sessionStorage.removeItem(LOBBY_SCOREBOARD_LOCK_KEY);
  } catch {
    /* ignore */
  }
}

export function peekLobbyRematchPending() {
  try {
    return sessionStorage.getItem(LOBBY_REMATCH_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

export function consumeLobbyRematchPending() {
  try {
    if (sessionStorage.getItem(LOBBY_REMATCH_PENDING_KEY) !== '1') return false;
    sessionStorage.removeItem(LOBBY_REMATCH_PENDING_KEY);
    return true;
  } catch {
    return false;
  }
}
